import { useState, useEffect, useRef } from 'react';
import { fetchCached, resolveImageUrl } from '@/lib/api';
import SectionWatermark from '@/components/SectionWatermark';
import { X, Image as ImageIcon } from 'lucide-react';

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return inView;
}

function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div ref={ref} className={className}
      style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(24px)', transition: `all 0.7s ease-out ${delay}ms` }}>
      {children}
    </div>
  );
}

export default function GalleryPage() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    fetchCached('/gallery', { ttlMs: 2 * 60 * 1000 }).then((data) => {
      setImages(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="gallery-page" className="pt-20">
      {/* Hero */}
      <section className="mscm-hero-panel mscm-cinematic-hero py-20 md:py-28">
        <div className="mscm-hero-aurora" />
        <div className="mscm-star-field" />
        <div className="mscm-luminous-sweep" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 text-center">
          <span className="text-white/85 text-xs font-bold uppercase tracking-[0.16em]">Million Stars Community Ministry</span>
          <h1 className="mscm-hero-title mt-4 font-bold text-white lg:text-6xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Gallery
          </h1>
          <p className="mt-4 text-white/80 text-base md:text-lg">
            Moments of faith, fellowship, and service
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <SectionWatermark
        className="mscm-section bg-white"
        contentClassName="max-w-7xl mx-auto px-6 md:px-8"
        watermarkClassName="mscm-watermark-strong"
        opacity={0.18}
      >
        {loading ? (
          <AnimatedSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="gallery-grid-loading">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="mscm-public-card mscm-public-skeleton aspect-[4/3]" />
              ))}
            </div>
          </AnimatedSection>
        ) : images.length === 0 ? (
          <AnimatedSection>
            <div className="mscm-premium-card mx-auto max-w-xl p-10 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#EAF4FF] text-[#1E4FA1] mb-6">
                <ImageIcon size={30} />
              </div>
              <span className="mscm-eyebrow text-[#1E4FA1]">Coming Soon</span>
              <h2 className="mt-3 text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Gallery images on their way
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-[#64748B] max-w-sm mx-auto">
                Moments of faith, fellowship, and service will appear here as the gallery grows.
              </p>
            </div>
          </AnimatedSection>
        ) : (
          <AnimatedSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="gallery-grid">
              {images.map((img, i) => (
                <button
                  key={img.id}
                  data-testid={`gallery-image-${i}`}
                  onClick={() => setLightbox(img)}
                  className="mscm-public-card group relative aspect-[4/3] bg-[#F8FAFC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4FA1]/30"
                  aria-label={img.caption ? `Open gallery image: ${img.caption}` : 'Open gallery image'}
                >
                  <img
                    src={resolveImageUrl(img.thumbnail_path || img.storage_path)}
                    alt={img.caption || 'Gallery image'}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                  {img.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-white text-sm">{img.caption}</p>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </AnimatedSection>
        )}
      </SectionWatermark>

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 bg-[#06183C]/95 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="lightbox-close"
            className="absolute top-4 right-4 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/10 p-2 text-white/80 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            onClick={() => setLightbox(null)}
            aria-label="Close gallery image"
          >
            <X size={28} />
          </button>
          <img
            src={resolveImageUrl(lightbox.storage_path)}
            alt={lightbox.caption || 'Gallery image'}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            loading="eager"
            decoding="async"
            onClick={(e) => e.stopPropagation()}
          />
          {lightbox.caption && (
            <p className="mscm-text-safe absolute bottom-8 text-white text-center w-full px-4 sm:px-8">{lightbox.caption}</p>
          )}
        </div>
      )}
    </div>
  );
}
